// R115 maint — the calendar's WO inspector (registerInspector('wo') replaces the
// core's lite one). Jesse A2: "clicking a WO on the calendar opens a popup with
// EVERYTHING you can do on the Maintenance tab" — so the panel carries the
// Maintenance card's whole surface, as sections of the ONE inspector (no
// z 9000 overlay, no page hop):
//   Summary (pills, who / booked / due, rename, instructions)   Details (the
//   shared WoEditor fieldsets + owner-facing summary, ONE diff → POST /:id)
//   Checklist · Tech report (+ mark done / follow-up + the After-evidence
//   picker) · Photos & video (before / after / receipt, through the persisted
//   background queue — R116 #5) · Comments · Labor time · Vendor flow · Owner
//   portal · Ground review · Field access · History
// and the head actions Book / Move (pickDay) · Mark done · Unbook · Reopen ·
// Open in Maintenance · Text vendor · Copy vendor link · Delete.
// Every write goes through the Maintenance module's existing route, tagged
// surface:'calendar' where the route reads it; errors show inline with the
// server's own words. Money (cost / payer / billed / skill / labor $ / quote,
// invoice and owner-portal amounts) renders only for admin / director (the
// server strips it from GET /work-orders/:id for every other role too).
import { registerInspector, registerSection, registerAction, type CalendarCtx, type EntityRef } from '../registry';
import { woEditFormHtml, readWoForm, rosterDatalistHtml, bindWoEditForm } from '../../../shared/components/WoEditor';
import { kebabHtml, bindKebabs } from '../../../shared/components/Kebab';
import { WoQuickView } from '../../../shared/components/WoQuickView';
import { mountTextVendor } from '../../../shared/components/TextVendorSheet';   // R116 #2: vendor group texts, in the calendar's sheet
import { vendorRepliesHtml } from '../../../shared/lib/vendorReplies';   // R116 #2: replies name their sender + photo thumbnails
import { enqueuePhotos, pendingItems } from '../../../lib/photoUploader';   // R116 #5: photos + clips, many at once, persisted
import { mediaStrip, bindMediaStrips, pendingTilesHtml, patchPendingProgress, mergeLandedMedia, isVideoUrl, EVIDENCE_ACCEPT, type MediaItem } from '../../../shared/lib/media';
import { evidencePickerHtml, bindEvidencePicker, type EvidencePickerCfg } from '../../../shared/components/EvidencePicker';
import {
  fmtDate, fmtDateTime, fmtMoney, fmtMins, schedOf, schedEndOf, dueOf, statusLabel, priorityOf, priorityLabel, woDoneAt,
  WO_BILLED_LABEL, isPlaceholderDesc,
} from '../../../shared/lib/woFormat';
import { diffWoEdit, applyWoEdit } from '../../../../../lib/woEdit.mjs';
import {
  api, AO, WO_API, LAYER_ID, OFFICE, ALL_VIEWS, seesMoney, loadVendors, loadRoster, loadAssets, uiOf, ui, hasUnsaved, anyWoBusy,
  PRIO_LABEL, wmd, md, type Vendor, type Asset, type Person, type WoUi,
} from './shared';
import { pickAndBook, bookWo, type BookTarget } from './book';

export interface WoData { wo: any | null; error: string; vendors: Vendor[]; assets: Asset[]; people: Person[]; field: any | null }

const esc = (s: unknown): string => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
const ROSTER_ID = 'mw-roster';
const isAdmin = (ctx: CalendarCtx) => ['admin', 'director'].includes(ctx.role);
const wantsField = (w: any) => { const p = priorityOf(w); return p === 'emergency' || p === 'urgent' || w.source === 'incident'; };
const target = (w: any): BookTarget => ({ id: String(w.id), listing_id: String(w.listing_id), title: String(w.title || 'Work order'), prev: schedOf(w), guest_ok: !!(w.guest_permission && w.guest_permission.granted), prev_window: w.scheduled_window && w.scheduled_window.start ? { start: String(w.scheduled_window.start).slice(0, 10), end: w.scheduled_window.end ? String(w.scheduled_window.end).slice(0, 10) : null } : null });

/** The record as the form currently shows it — what the fieldsets render from. R115 review: only the fields the
 *  user CHANGED are overlaid (u.edits), so a fresh record (a booking, Mark done, a colleague's edit) shows through
 *  every untouched field and Save never sends a stale snapshot back. */
function shown(w: any, u: WoUi): any {
  return u.edits ? applyWoEdit(w, u.edits) : w;
}

/** After a write: merge what the route returned and reload (the calendar too when the day / chip changed). */
async function landed(ctx: CalendarCtx, data: WoData, fresh: any, calendar: boolean): Promise<void> {
  if (fresh && data.wo && String(fresh.id) === String(data.wo.id)) data.wo = { ...data.wo, ...fresh };
  ctx.markDirty(woEditorBusy());
  try { await ctx.reload({ inspector: true, base: calendar, layers: calendar ? [LAYER_ID] : undefined }); } catch { /* page shows its own error */ }
}
/** One write with busy + inline error handling. `onOk` settles the UI state (clears the draft,
 *  patches the record) BEFORE the reload re-renders the panel, so a re-render never shows a
 *  posted comment still sitting in its box. */
async function write(ctx: CalendarCtx, data: WoData, host: HTMLElement, redraw: () => void, busyKey: string, method: string, url: string, body: any, calendar: boolean, onOk?: (j: any, u: WoUi) => void): Promise<any | null> {
  const w = data.wo; if (!w) return null;
  const u = uiOf(String(w.id));
  if (u.busy) return null;
  u.busy = busyKey; u.err = ''; u.ok = ''; redraw();
  const r = await api(method, url, body);
  u.busy = '';
  if (!r.ok) { u.err = r.error; redraw(); return null; }
  const j = r.json || {};
  if (onOk) { try { onOk(j, u); } catch { /* UI bookkeeping only */ } }
  await landed(ctx, data, j.work_order, calendar);
  if (host.isConnected) redraw();
  return j;
}

// ── section plumbing: each section owns a wrapper it can redraw locally ─────
type Inner = (w: any, data: WoData, ctx: CalendarCtx) => string;
type Binder = (host: HTMLElement, w: any, data: WoData, ctx: CalendarCtx, redraw: () => void) => void;
function section(key: string, title: string, order: number, when: (w: any, data: WoData, ctx: CalendarCtx) => boolean, inner: Inner, bind: Binder, open = false): void {
  registerSection({
    inspector: 'wo', key, title, order, openIn: open ? ALL_VIEWS : undefined,
    when: (_ref, data, ctx) => { const d = data as WoData; return !!(d && d.wo) && when(d.wo, d, ctx); },
    render: (_ref, data, ctx) => { const d = data as WoData; return `<div class="mw" data-mw-sec="${key}">${inner(d.wo, d, ctx)}</div>`; },
    bind: (root, _ref, data, ctx) => {
      const d = data as WoData;
      const host = (root.querySelector<HTMLElement>(`[data-mw-sec="${key}"]`) || root);
      const redraw = () => { if (!d.wo) return; host.innerHTML = inner(d.wo, d, ctx); bind(host, d.wo, d, ctx, redraw); bindKebabs(host); };
      bind(host, d.wo, d, ctx, redraw);
      bindKebabs(host);
    },
    busy: () => anyWoBusy(),
  });
}
const msgLine = (u: WoUi) => `${u.err ? `<div class="mw-err" role="alert">${esc(u.err)}</div>` : ''}${u.ok ? `<div class="mw-ok">${esc(u.ok)}</div>` : ''}`;
const pill = (text: string, tone = '') => `<span class="mw-pill ${tone}">${text}</span>`;

// ── the inspector ───────────────────────────────────────────────────────────
registerInspector({
  kind: 'wo',
  title: (_ref, data) => { const d = data as WoData | null; return d && d.wo ? String(d.wo.title || 'Work order') : 'Work order'; },
  sub: (ref, data, ctx) => {
    const d = data as WoData | null;
    if (!d || !d.wo) return d && d.error ? d.error : '';
    const w = d.wo;
    const p = ctx.property(String(w.listing_id));
    return [p ? (p.name || w.listing_name) : w.listing_name, priorityLabel(priorityOf(w)), statusLabel(w.status)].filter(Boolean).join(' · ');
  },
  load: async (ref: EntityRef, signal: AbortSignal): Promise<WoData> => {
    const id = String(ref.id);
    const [r, vendors, people] = await Promise.all([api('GET', `${WO_API}/${encodeURIComponent(id)}`, undefined, signal), loadVendors(), loadRoster()]);
    if (!r.ok || !r.json || !r.json.work_order) {
      return { wo: null, error: r.status === 404 ? 'Work order not found — it may have been deleted.' : r.status === 403 ? "Your role can't open work orders." : `Couldn't load this work order — ${r.error}`, vendors, assets: [], people, field: null };
    }
    const wo = r.json.work_order;
    const [assets, field] = await Promise.all([
      loadAssets(String(wo.listing_id)),
      wantsField(wo) ? api('GET', `${AO}/maintenance/field/${encodeURIComponent(String(wo.listing_id))}`, undefined, signal).then(x => (x.ok ? x.json : null)).catch(() => null) : Promise.resolve(null),
    ]);
    return { wo, error: '', vendors, assets, people, field };
  },
  busy: () => anyWoBusy() || opAsk.size > 0,   // an open owner-portal ask counts — closing asks first
  // closing the panel discards unsaved edits (the page asks before closing a busy inspector). R115 review: the
  // owner-portal ask and an armed revoke go too, and an entry whose write is still in flight keeps only its busy
  // flag (no drafts) — nothing left behind can park the auto-refresh veto.
  reset: () => {
    opAsk.clear(); revokeArmed.clear();
    for (const [id, u] of ui) {
      if (!u.busy) { ui.delete(id); continue; }
      u.form = null; u.edits = null; u.dirty = false; u.cmt = ''; u.report = ''; u.fu = false; u.fuNote = ''; u.reviewNote = ''; u.itemNote = {}; u.editTitle = null; u.editDesc = null;
    }
  },
});

// an inspector whose load failed still says why
registerSection({
  inspector: 'wo', key: 'wo_error', title: 'Work order', order: 1, openIn: ALL_VIEWS,
  when: (_r, data) => { const d = data as WoData | null; return !!(d && !d.wo); },
  render: (_r, data) => `<div class="mw"><div class="mw-err" role="alert">${esc((data as WoData | null)?.error || "Couldn't load this work order.")}</div></div>`,
});

// ── 1. Summary ──────────────────────────────────────────────────────────────
section('wo_summary', 'Summary', 10, () => true, (w, data, ctx) => {
  const u = uiOf(String(w.id));
  const money = seesMoney(ctx);
  const prio = priorityOf(w);
  const today = ctx.asOf;
  const sched = schedOf(w), schedEnd = schedEndOf(w), due = dueOf(w), doneAt = woDoneAt(w);
  const op = w.owner_portal && w.owner_portal.state ? String(w.owner_portal.state) : '';
  const OP: Record<string, [string, string]> = { published: ['🏦 on owner portal', 'info'], needs_approval: ['🏦 awaiting owner', 'warn'], approved: ['🏦 owner approved', 'ok'], declined: ['🏦 owner declined', 'bad'] };
  const who = w.vendor_name || w.assigned_to || '';
  const row = (k: string, v: string, cls = '') => `<div class="mw-row"><span class="mw-k">${k}</span><span class="mw-v ${cls}">${v}</span></div>`;
  const desc = isPlaceholderDesc(w.description) ? '' : String(w.description || '');
  const mins = Number(w.total_minutes) || 0;
  return `
    <div class="mw-pills">
      ${pill(w.type === 'pm' ? 'PM' : 'WO', 'acc')}${pill(esc(PRIO_LABEL[prio] || prio), prio === 'emergency' ? 'bad' : prio === 'urgent' ? 'warn' : '')}${pill(esc(statusLabel(w.status)), w.status === 'done' ? 'ok' : w.status === 'in_progress' ? 'warn' : '')}
      ${op && OP[op] ? pill(OP[op][0], OP[op][1]) : ''}${w.claim_tracked ? pill('⚖ claim tracked', 'info') : ''}
      ${w.completion && w.completion.needs_followup && w.status !== 'done' ? pill('🔁 follow-up', 'warn') : ''}
      ${w.role && w.role !== 'maintenance' ? pill(esc(w.role === 'cleaner' ? 'Cleaner job' : 'Inspector job'), '') : ''}
    </div>
    ${u.editTitle !== null
      ? `<div class="mw-inline"><input type="text" class="mw-input" data-mw-title-input value="${esc(u.editTitle)}" maxlength="200" aria-label="Work order title"><button type="button" class="btn btn-secondary btn-sm" data-mw-title-save ${u.busy ? 'disabled' : ''}>${u.busy === 'title' ? 'Saving…' : 'Save'}</button><button type="button" class="btn btn-ghost btn-sm" data-mw-title-cancel>Cancel</button></div>`
      : `<div class="mw-inline"><button type="button" class="btn btn-ghost btn-sm" data-mw-title-edit>✏️ Rename</button></div>`}
    <div class="mw-kv">
      ${row('Who', who ? esc(who) : '<span class="mw-muted">nobody assigned</span>')}
      ${row('Booked', sched ? `${esc(wmd(sched))}${schedEnd ? ` → ${esc(md(schedEnd))}` : ''}${sched < today && w.status !== 'done' ? ' <span class="mw-muted">· was booked</span>' : ''}` : '<span class="mw-muted">not booked</span>', sched && sched < today && w.status !== 'done' ? 'late' : '')}
      ${row('Due', due.source === 'checkout' ? `🔁 at next checkout${due.date ? ` (${esc(md(due.date))})` : ''}` : due.date ? esc(wmd(due.date)) : '<span class="mw-muted">no due date</span>', due.date && due.date < today && w.status !== 'done' ? 'late' : '')}
      ${money ? row('Cost', w.cost != null && Number(w.cost) !== 0 ? `${esc(fmtMoney(w.cost))}${w.payer ? ` <span class="mw-muted">· ${w.payer === 'owner' ? 'owner pays' : 'WCH pays'}</span>` : ''}` : '<span class="mw-muted">—</span>') : ''}
      ${money && w.payer === 'owner' ? row('Billed', w.billing_status ? esc((WO_BILLED_LABEL as any)[w.billing_status] || 'billed') : '<span class="mw-pill warn">unbilled</span>') : ''}
      ${w.guest_permission && w.guest_permission.granted ? row('Guest access', `🚪 ${w.guest_permission.mode === 'own_code' ? 'enter with own code' : 'guest will be present'}`) : ''}
      ${w.created_by ? row('Submitted by', esc(w.created_by)) : ''}
      ${row(doneAt ? 'Completed' : 'Opened', esc(doneAt ? fmtDateTime(doneAt) : fmtDate(w.created_at)))}
      ${mins || w.timer_started_at ? row('Labor', `${esc(fmtMins(mins))}${w.timer_started_at ? ' <span class="mw-muted">· ⏱ running</span>' : ''}`) : ''}
    </div>
    <div class="mw-block">
      <div class="mw-h">Instructions ${u.editDesc === null ? '<button type="button" class="mw-link" data-mw-desc-edit aria-label="Edit instructions">✎ edit</button>' : ''}</div>
      ${u.editDesc !== null
        ? `<textarea class="mw-input" rows="4" data-mw-desc-input placeholder="What needs to get done, where, what you need after…">${esc(u.editDesc)}</textarea>
           <div class="mw-inline"><button type="button" class="btn btn-secondary btn-sm" data-mw-desc-save ${u.busy ? 'disabled' : ''}>${u.busy === 'desc' ? 'Saving…' : 'Save'}</button><button type="button" class="btn btn-ghost btn-sm" data-mw-desc-cancel>Cancel</button></div>`
        : (desc ? `<div class="mw-text">${esc(desc)}</div>` : '<div class="mw-muted">No instructions yet.</div>')}
    </div>
    ${w.money_hidden ? '<div class="mw-fine">Cost and billing are hidden for your role — an admin or director keeps them.</div>' : ''}
    ${msgLine(u)}`;
}, (host, w, data, ctx, redraw) => {
  const u = uiOf(String(w.id));
  const q = <T extends Element>(s: string) => host.querySelector<T>(s);
  q('[data-mw-title-edit]')?.addEventListener('click', () => { u.editTitle = String(w.title || ''); redraw(); q<HTMLInputElement>('[data-mw-title-input]')?.focus(); });
  q('[data-mw-title-cancel]')?.addEventListener('click', () => { u.editTitle = null; ctx.markDirty(anyWoBusy()); redraw(); });
  const ti = q<HTMLInputElement>('[data-mw-title-input]');
  ti?.addEventListener('input', () => { u.editTitle = ti.value; ctx.markDirty(true); });
  ti?.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); q<HTMLButtonElement>('[data-mw-title-save]')?.click(); } });
  q('[data-mw-title-save]')?.addEventListener('click', async () => {
    const title = String(u.editTitle || '').trim();
    if (!title) { u.err = 'A work order needs a title.'; redraw(); return; }
    if (title === String(w.title || '').trim()) { u.editTitle = null; redraw(); return; }
    const j = await write(ctx, data, host, redraw, 'title', 'POST', `${WO_API}/${encodeURIComponent(w.id)}`, { title, by: ctx.actorName(), surface: 'calendar' }, true, () => { u.editTitle = null; if (data.wo) data.wo.title = title; });
    if (j) ctx.toast('Renamed');
  });
  q('[data-mw-desc-edit]')?.addEventListener('click', () => { u.editDesc = String(w.description || ''); redraw(); q<HTMLTextAreaElement>('[data-mw-desc-input]')?.focus(); });
  q('[data-mw-desc-cancel]')?.addEventListener('click', () => { u.editDesc = null; ctx.markDirty(anyWoBusy()); redraw(); });
  const di = q<HTMLTextAreaElement>('[data-mw-desc-input]');
  di?.addEventListener('input', () => { u.editDesc = di.value; ctx.markDirty(true); });
  q('[data-mw-desc-save]')?.addEventListener('click', async () => {
    const description = String(u.editDesc ?? '').trim();
    await write(ctx, data, host, redraw, 'desc', 'POST', `${WO_API}/${encodeURIComponent(w.id)}`, { description, by: ctx.actorName(), surface: 'calendar' }, false, () => { u.editDesc = null; if (data.wo) data.wo.description = description; });
  });
}, true);

// ── 2. Details: the shared fieldsets + owner-facing summary, ONE diff ───────
section('wo_edit', 'Details', 20, () => true, (w, data, ctx) => {
  const u = uiOf(String(w.id));
  const money = seesMoney(ctx);
  const view = shown(w, u);
  return `
    ${rosterDatalistHtml(ROSTER_ID, data.people, data.vendors, w.assigned_to ? [String(w.assigned_to)] : [])}
    <div class="mw-edit">${woEditFormHtml(view, { money, vendors: data.vendors, assets: data.assets, rosterId: ROSTER_ID })}</div>
    <label class="mw-h" for="mw-sum-${esc(w.id)}">Owner-facing summary <span class="mw-muted">— the “maintenance done” record</span></label>
    <textarea id="mw-sum-${esc(w.id)}" class="mw-input" rows="3" data-mn-summary="${esc(w.id)}" placeholder="What was done, in owner-readable language. Becomes part of the owner report.">${esc(view.summary || '')}</textarea>
    <div class="mw-bar">
      <button type="button" class="btn btn-secondary mw-save ${u.dirty ? 'mw-save-on' : ''}" data-mw-save ${u.busy ? 'disabled' : ''}>${u.busy === 'save' ? 'Saving…' : 'Save changes'}</button>
      ${u.dirty ? '<button type="button" class="btn btn-ghost" data-mw-discard>Discard</button><span class="mw-dirty">Unsaved changes</span>' : ''}
    </div>
    ${money ? '<p class="mw-fine">Cost &amp; payer are Asset Ops\' own record — not synced to owner statements (those are generated externally).</p>' : ''}
    ${msgLine(u)}`;
}, (host, w, data, ctx, redraw) => {
  const u = uiOf(String(w.id));
  const money = seesMoney(ctx);
  const id = String(w.id);
  const capture = () => {
    const f = readWoForm(host, id);
    u.form = f;
    const was = u.dirty;
    u.edits = diffWoEdit(w, f, { money });   // against the record this form was drawn from (w), never a stale copy
    u.dirty = u.edits !== null;
    if (u.dirty !== was) {
      const bar = host.querySelector('.mw-bar');
      if (bar) { const save = bar.querySelector('[data-mw-save]'); bar.innerHTML = ''; if (save) { save.classList.toggle('mw-save-on', u.dirty); bar.appendChild(save); } if (u.dirty) { bar.insertAdjacentHTML('beforeend', '<button type="button" class="btn btn-ghost" data-mw-discard>Discard</button><span class="mw-dirty">Unsaved changes</span>'); bindDiscard(); } }
    }
    ctx.markDirty(anyWoBusy());
  };
  const bindDiscard = () => host.querySelector('[data-mw-discard]')?.addEventListener('click', () => { u.form = null; u.edits = null; u.dirty = false; u.err = ''; ctx.markDirty(anyWoBusy()); redraw(); });
  bindWoEditForm(host, id, capture);
  bindDiscard();
  host.querySelector('[data-mw-save]')?.addEventListener('click', async () => {
    const body = diffWoEdit(w, readWoForm(host, id), { money });
    if (!body) { u.ok = 'Nothing changed.'; u.err = ''; u.form = null; u.edits = null; u.dirty = false; ctx.markDirty(anyWoBusy()); redraw(); return; }
    const calendarChange = ['scheduled_date', 'status', 'priority', 'assigned_to', 'vendor_id', 'due_date', 'due_source'].some(k => k in body);
    const j = await write(ctx, data, host, redraw, 'save', 'POST', `${WO_API}/${encodeURIComponent(id)}`, { ...body, by: ctx.actorName(), surface: 'calendar' }, calendarChange, (r, uu) => {
      if (!r.work_order && data.wo) data.wo = applyWoEdit(data.wo, body);
      uu.form = null; uu.edits = null; uu.dirty = false; uu.ok = 'Saved.';
    });
    if (j) ctx.toast('Work order saved');
  });
}, true);

// ── 3. Checklist ───────────────────────────────────────────────────────────
section('wo_checklist', 'Checklist', 30, (w) => Array.isArray(w.items) && w.items.length > 0, (w) => {
  const u = uiOf(String(w.id));
  const items: any[] = w.items || [];
  const done = items.filter(i => i && i.done).length;
  return `
    <div class="mw-h">${done}/${items.length} done</div>
    <ul class="mw-items">${items.map(it => `
      <li class="mw-item">
        <button type="button" class="mw-tick ${it.done ? 'on' : ''}" data-mw-item-toggle="${esc(it.key)}" aria-pressed="${it.done ? 'true' : 'false'}" aria-label="${it.done ? 'Mark not done' : 'Mark done'}: ${esc(it.title)}" ${u.busy ? 'disabled' : ''}>${it.done ? '✓' : ''}</button>
        <div class="mw-item-b">
          <div class="mw-item-t">${esc(it.title)}</div>
          ${it.instructions ? `<div class="mw-muted">${esc(it.instructions)}</div>` : ''}
          ${it.note ? `<div class="mw-item-note">“${esc(it.note)}”</div>` : ''}
          ${itemMedia(it)}
          ${u.itemNote[it.key] !== undefined
            ? `<div class="mw-inline"><input type="text" class="mw-input" data-mw-item-note-input="${esc(it.key)}" value="${esc(u.itemNote[it.key])}" placeholder="Note for this step"><button type="button" class="btn btn-secondary btn-sm" data-mw-item-note-save="${esc(it.key)}">Save</button><button type="button" class="btn btn-ghost btn-sm" data-mw-item-note-cancel="${esc(it.key)}">Cancel</button></div>`
            : `<div class="mw-item-acts"><button type="button" class="mw-link" data-mw-item-note="${esc(it.key)}">${it.note ? 'Edit note' : 'Add note'}</button><button type="button" class="mw-link" data-mw-item-photo="${esc(it.key)}">Attach photos / video</button></div>`}
        </div>
      </li>`).join('')}</ul>
    <input type="file" accept="${EVIDENCE_ACCEPT}" multiple hidden data-mw-item-file>
    ${msgLine(u)}`;
}, (host, w, data, ctx, redraw) => {
  const u = uiOf(String(w.id));
  const id = String(w.id);
  host.querySelectorAll<HTMLElement>('[data-mw-item-toggle]').forEach(el => el.addEventListener('click', () => {
    const key = el.dataset.mwItemToggle!;
    const it = (w.items || []).find((x: any) => x && x.key === key);
    void write(ctx, data, host, redraw, 'item', 'POST', `${WO_API}/${encodeURIComponent(id)}`, { items: [{ key, done: !(it && it.done) }], by: ctx.actorName(), surface: 'calendar' }, false);
  }));
  host.querySelectorAll<HTMLElement>('[data-mw-item-note]').forEach(el => el.addEventListener('click', () => {
    const key = el.dataset.mwItemNote!; const it = (w.items || []).find((x: any) => x && x.key === key);
    u.itemNote[key] = String((it && it.note) || ''); redraw(); host.querySelector<HTMLInputElement>(`[data-mw-item-note-input="${CSS.escape(key)}"]`)?.focus();
  }));
  host.querySelectorAll<HTMLInputElement>('[data-mw-item-note-input]').forEach(el => el.addEventListener('input', () => { u.itemNote[el.dataset.mwItemNoteInput!] = el.value; ctx.markDirty(true); }));
  host.querySelectorAll<HTMLElement>('[data-mw-item-note-cancel]').forEach(el => el.addEventListener('click', () => { delete u.itemNote[el.dataset.mwItemNoteCancel!]; ctx.markDirty(anyWoBusy()); redraw(); }));
  host.querySelectorAll<HTMLElement>('[data-mw-item-note-save]').forEach(el => el.addEventListener('click', async () => {
    const key = el.dataset.mwItemNoteSave!;
    await write(ctx, data, host, redraw, 'item', 'POST', `${WO_API}/${encodeURIComponent(id)}`, { items: [{ key, note: String(u.itemNote[key] || '') }], by: ctx.actorName(), surface: 'calendar' }, false, () => { delete u.itemNote[key]; });
  }));
  const file = host.querySelector<HTMLInputElement>('[data-mw-item-file]');
  host.querySelectorAll<HTMLElement>('[data-mw-item-photo]').forEach(el => el.addEventListener('click', () => {
    if (!file || file.disabled) return;
    file.onchange = () => { const fs = Array.from(file.files || []); file.value = ''; if (fs.length) void uploadPhoto(ctx, data, file, fs, { item_key: el.dataset.mwItemPhoto! }); };
    file.click();
  }));
  bindMediaStrips(host);
  onWoMedia(String(w.id), host, redraw);
});

// ── 4. Tech report (+ mark done / follow-up) ───────────────────────────────
section('wo_report', 'Tech report', 35, (w) => !!w.completion || w.status !== 'done', (w, data, ctx) => {
  const u = uiOf(String(w.id));
  const c = w.completion;
  if (c && c.what_done) {
    return `<div class="mw-done-rep">
        <div class="mw-h">${esc(String(c.by || 'someone').split('@')[0])} · ${esc(fmtDateTime(c.at))}</div>
        <div class="mw-text">${esc(c.what_done)}</div>
        <div class="mw-muted">⏱ ${esc(fmtMins(w.total_minutes))}</div>
        ${c.needs_followup ? `<div class="mw-warn">🔁 Follow-up needed${c.followup_note ? ` — ${esc(c.followup_note)}` : ''}</div>` : ''}
      </div>
      ${w.status !== 'done' ? '<div class="mw-fine">Filed a report but still open — mark it done from the head of the panel when the follow-up is handled.</div>' : ''}`;
  }
  return `<div data-evp-scope>
    <label class="mw-h" for="mw-rep-${esc(w.id)}">What was done</label>
    <textarea id="mw-rep-${esc(w.id)}" class="mw-input" rows="3" data-mw-report placeholder="What did you do? Anything the team should know?">${esc(u.report)}</textarea>
    <label class="mw-check"><input type="checkbox" data-mw-fu ${u.fu ? 'checked' : ''}> 🔁 More work / inspection needed</label>
    ${u.fu ? `<input type="text" class="mw-input" data-mw-funote value="${esc(u.fuNote)}" placeholder="What's still needed — part on order, needs inspection, owner approval…">` : ''}
    ${evidencePickerHtml(afterPickerCfg(w, data, ctx))}
    <div class="mw-bar"><button type="button" class="btn btn-secondary" data-mw-file ${u.busy || !u.report.trim() ? 'disabled' : ''}>${u.busy === 'report' ? 'Filing…' : (u.fu ? '📋 File report — keep open' : '✅ File report & mark done')}</button></div>
    ${msgLine(u)}
  </div>`;
}, (host, w, data, ctx, redraw) => {
  const u = uiOf(String(w.id));
  const ta = host.querySelector<HTMLTextAreaElement>('[data-mw-report]');
  const btn = host.querySelector<HTMLButtonElement>('[data-mw-file]');
  ta?.addEventListener('input', () => { u.report = ta.value; if (btn) btn.disabled = !!u.busy || !ta.value.trim(); ctx.markDirty(anyWoBusy()); });
  host.querySelector<HTMLInputElement>('[data-mw-fu]')?.addEventListener('change', (e) => { u.fu = (e.target as HTMLInputElement).checked; redraw(); });
  const fn = host.querySelector<HTMLInputElement>('[data-mw-funote]');
  fn?.addEventListener('input', () => { u.fuNote = fn.value; ctx.markDirty(true); });
  btn?.addEventListener('click', async () => {
    const what_done = u.report.trim();
    if (!what_done) { ta?.focus(); return; }
    const fu = u.fu;
    const j = await write(ctx, data, host, redraw, 'report', 'POST', `${WO_API}/${encodeURIComponent(w.id)}`, {
      completion: { what_done, needs_followup: u.fu, followup_note: u.fuNote.trim() }, status: u.fu ? 'open' : 'done', by: ctx.actorName(), surface: 'calendar',
    }, true, () => { u.report = ''; u.fu = false; u.fuNote = ''; });
    // R116 #5: filing never waits for the evidence — say what's still on its way
    const still = pendingItems(`${WO_API}/${encodeURIComponent(w.id)}/photo`).length;
    if (j) ctx.toast(`${fu ? 'Report filed — still open for the follow-up' : 'Report filed — work order done'}${still ? ` · ${still} file${still === 1 ? '' : 's'} still uploading` : ''}`);
  });
  if (host.querySelector('[data-evp]')) bindEvidencePicker(host, afterPickerCfg(w, data, ctx));
  bindMediaStrips(host);
});

// ── 5. Photos & video (plain / before / after / receipt) ───────────────────
// R116 #5 (Jorge: video evidence + many at once): every pick rides the
// persisted background queue (lib/photoUploader — one file per request,
// photos 3-wide / clips 1-wide, client_key, the server's size cap), tagged
// with who added it; nothing here waits for the upload. When a file lands the
// sections that show media redraw from the server's record (no panel reload).
async function uploadPhoto(ctx: CalendarCtx, data: WoData, input: HTMLInputElement, files: File[], fields: Record<string, string>): Promise<void> {
  const w = data.wo; if (!w || !files.length) return;
  input.disabled = true;   // double-pick guard
  try {
    await enqueuePhotos(`${WO_API}/${encodeURIComponent(w.id)}/photo`, files, { ...fields, by: ctx.actorName(), surface: 'calendar' }, fields.kind === 'receipt' ? 'receipt' : 'work-order photo');
  } finally { input.disabled = false; }
  redrawWoMedia(String(w.id));
}
const woPhotoUrl = (id: string) => `${WO_API}/${encodeURIComponent(id)}/photo`;
/** A checklist item's photos + clips as one strip. */
function itemMedia(it: any): string {
  const vids: string[] = Array.isArray(it && it.videos) ? it.videos.filter((v: any) => typeof v === 'string') : [];
  const items: MediaItem[] = [
    ...(Array.isArray(it && it.photos) ? it.photos : []).filter((u: any) => typeof u === 'string' && u).map((u: string) => ({ url: u, video: isVideoUrl(u) })),
    ...vids.map(u => ({ url: u, video: true })),
  ];
  return items.length ? mediaStrip(items, { size: 'sm', alt: 'checklist photo' }) : '';
}
function mediaCaption(m: any, label: string): string {
  const by = String((m && m.by) || '').replace(/^vendor:/, 'vendor · ').split('@')[0];
  return [label, by, m && m.at ? fmtDateTime(m.at) : ''].filter(Boolean).join(' · ');
}
/** The WO's evidence by kind — photos AND clips (photos_meta.media / the url says which). */
function mediaByKind(w: any): { before: MediaItem[]; after: MediaItem[]; receipt: MediaItem[]; plain: MediaItem[] } {
  const meta: any[] = Array.isArray(w.photos_meta) ? w.photos_meta : [];
  const vids = new Set<string>((Array.isArray(w.videos) ? w.videos : []).filter((v: any) => typeof v === 'string'));
  const isVid = (m: any, u: string) => (m && m.media === 'video') || vids.has(u) || isVideoUrl(u);
  const byKind = (k: string, label: string): MediaItem[] => {
    const seen = new Set<string>();
    return meta.filter(m => m && m.kind === k && m.url && !seen.has(m.url) && seen.add(m.url)).map(m => ({ url: String(m.url), video: isVid(m, String(m.url)), caption: mediaCaption(m, label) }));
  };
  const kinded = new Set<string>(meta.filter(m => m && m.kind && m.url).map(m => String(m.url)));
  const plain = [...new Set([...(Array.isArray(w.photos) ? w.photos : []), ...vids].filter((u: any) => typeof u === 'string' && u && !kinded.has(u)))]
    .map((u: string) => { const m = meta.find(x => x && x.url === u); return { url: u, video: isVid(m, u), caption: mediaCaption(m, 'Photo') }; });
  return { before: byKind('before', 'Before'), after: byKind('after', 'After'), receipt: byKind('receipt', 'Receipt'), plain };
}
/** The Tech report form's "After evidence" picker. */
function afterPickerCfg(w: any, data: WoData, ctx: CalendarCtx): EvidencePickerCfg {
  const id = String(w.id);
  return {
    id: `mw-after-${id}`,
    title: 'After evidence',
    url: woPhotoUrl(id),
    fields: { kind: 'after', by: ctx.actorName(), surface: 'calendar' },
    match: (f) => f.kind === 'after' && !f.item_key,
    existing: mediaByKind(w).after,
    label: 'after photo',
    hint: 'Photos or a short video of the finished work — they keep uploading after you file the report.',
    onUploaded: (resp: any) => {
      if (resp && resp.work_order && data.wo && String(data.wo.id) === id) data.wo = mergeLandedMedia({ ...data.wo }, resp.work_order);
      redrawWoMedia(id, 'wo_report');
      return data.wo ? mediaByKind(data.wo).after : undefined;
    },
  };
}
// redraw hooks for the sections that show a WO's media (pruned when their host leaves the DOM)
const mediaHooks = new Map<string, Map<HTMLElement, () => void>>();
function onWoMedia(id: string, host: HTMLElement, redraw: () => void): void {
  let m = mediaHooks.get(id);
  if (!m) { m = new Map(); mediaHooks.set(id, m); }
  for (const h of [...m.keys()]) if (!h.isConnected) m.delete(h);
  m.set(host, redraw);
}
function redrawWoMedia(id: string, except?: string): void {
  const m = mediaHooks.get(id);
  if (!m) return;
  for (const [h, fn] of [...m]) {
    if (!h.isConnected) { m.delete(h); continue; }
    if (except && h.closest(`[data-mw-sec="${except}"]`)) continue;
    try { fn(); } catch { /* one section's redraw never blocks another */ }
  }
}
let mediaListening = false;
function listenWoMedia(): void {
  if (mediaListening || typeof window === 'undefined') return;
  mediaListening = true;
  const idOf = (url: string) => { const x = /\/work-orders\/([^/]+)\/photo$/.exec(String(url || '')); return x ? decodeURIComponent(x[1]) : ''; };
  window.addEventListener('surge-photo-uploaded', (e: Event) => {
    const d = (e as CustomEvent).detail || {};
    const id = idOf(d.url);
    if (!id || !mediaHooks.has(id)) return;
    const fresh = d.response && d.response.work_order;
    const ref = currentData.get(id);
    if (fresh && ref && ref.wo && String(ref.wo.id) === id) ref.wo = mergeLandedMedia({ ...ref.wo }, fresh);   // answers can land out of order — never lose a file
    redrawWoMedia(id);
  });
  const sig = new Map<string, string>();   // only WOs whose queued files changed (a redraw reloads every clip poster)
  window.addEventListener('surge-photo-queue', () => {
    for (const id of mediaHooks.keys()) {
      const now = pendingItems(woPhotoUrl(id)).map(p => p.key).join(',');
      if ((sig.get(id) || '') === now) continue;
      sig.set(id, now);
      redrawWoMedia(id, 'wo_report');
    }
  });
  window.addEventListener('surge-photo-progress', (e: Event) => {
    const d = (e as CustomEvent).detail || {};
    const id = idOf(d.url);
    const m = id ? mediaHooks.get(id) : null;
    if (m) for (const h of m.keys()) if (h.isConnected) patchPendingProgress(h, String(d.clientKey || ''), Number(d.sent) || 0, Number(d.total) || 0);
  });
}
// the WoData object the panel last rendered each WO from (a landed upload merges into it)
const currentData = new Map<string, WoData>();
section('wo_photos', 'Photos & video', 40, () => true, (w) => {
  const u = uiOf(String(w.id));
  const g = mediaByKind(w);
  const group = (label: string, items: MediaItem[], alt: string) => items.length ? `<div class="mw-h">${label} <span class="mw-muted">${items.length}</span></div>${mediaStrip(items, { size: 'md', alt, cls: alt === 'after photo' ? 'mx-after' : '' })}` : '';
  const pickerOpen = !w.completion && w.status !== 'done';
  const pend = pendingItems(woPhotoUrl(String(w.id)), (f) => !(pickerOpen && f.kind === 'after' && !f.item_key));
  const any = g.before.length || g.after.length || g.receipt.length || g.plain.length || pend.length;
  return `
    ${group('📷 Before', g.before, 'before photo')}${group('✅ After', g.after, 'after photo')}${group('🧾 Receipts', g.receipt, 'receipt')}${group(g.plain.some(m => m.video) ? 'Photos &amp; video' : 'Photos', g.plain, 'work-order photo')}
    ${pend.length ? `<div class="mw-h">⏳ Uploading <span class="mw-muted">${pend.length}</span></div><div class="mx-strip mx-md" data-mx-strip>${pendingTilesHtml(pend)}</div>` : ''}
    ${any ? '' : '<div class="mw-muted">No photos or video yet.</div>'}
    <div class="mw-bar mw-wrap">
      <button type="button" class="btn btn-secondary btn-sm" data-mw-photo="">📷 Photos / video</button>
      <button type="button" class="btn btn-secondary btn-sm" data-mw-photo="before">Before</button>
      <button type="button" class="btn btn-secondary btn-sm" data-mw-photo="after">After</button>
      <button type="button" class="btn btn-secondary btn-sm" data-mw-photo="receipt" title="Receipts ride the WO so it can be coded and billed">🧾 Receipt</button>
    </div>
    <div class="mw-fine">Pick several at once — photos or video. They upload in the background, even if you close this panel.</div>
    <input type="file" accept="${EVIDENCE_ACCEPT}" multiple hidden data-mw-file-input>
    ${msgLine(u)}`;
}, (host, w, data, ctx, redraw) => {
  listenWoMedia();
  currentData.set(String(w.id), data);
  const file = host.querySelector<HTMLInputElement>('[data-mw-file-input]');
  host.querySelectorAll<HTMLElement>('[data-mw-photo]').forEach(el => el.addEventListener('click', () => {
    if (!file || file.disabled) return;
    const kind = el.dataset.mwPhoto || '';
    file.onchange = () => { const fs = Array.from(file.files || []); file.value = ''; if (fs.length) void uploadPhoto(ctx, data, file, fs, kind ? { kind } : {}); };
    file.click();
  }));
  bindMediaStrips(host);
  onWoMedia(String(w.id), host, redraw);
});

// ── 6. Comments (accounting ↔ ground team, never owners or vendors) ────────
section('wo_comments', 'Comments', 50, () => true, (w, _data, ctx) => {
  const u = uiOf(String(w.id));
  const legacy: any[] = (Array.isArray(w.internal_notes) ? w.internal_notes : []).map((n: any) => ({ ...n, legacy: true }));
  const list: any[] = [...legacy, ...(Array.isArray(w.comments) ? w.comments : [])].sort((a, b) => String(a.at || '').localeCompare(String(b.at || '')));
  const admin = isAdmin(ctx);
  return `
    <div class="mw-muted">Accounting ↔ ground team · never shown to owners or vendors</div>
    <div class="mw-thread">${list.slice(-30).map(c => `
      <div class="mw-cmt"><div class="mw-cmt-h"><b>${esc(c.by || '—')}</b>${c.legacy ? ' · note' : (c.role ? ` · ${esc(String(c.role).replace('_', ' '))}` : '')} · ${esc(String(c.at || '').slice(0, 16).replace('T', ' '))}${admin && !c.legacy && c.id ? ` <button type="button" class="mw-link mw-x" data-mw-cmt-rm="${esc(c.id)}" aria-label="Remove this comment">✕</button>` : ''}</div><div class="mw-text">${esc(c.text)}</div></div>`).join('') || '<div class="mw-muted">No comments yet.</div>'}</div>
    <div class="mw-compose">
      <textarea class="mw-input" rows="2" data-mw-cmt placeholder="Comment for the team… (⌘/Ctrl+Enter posts)">${esc(u.cmt)}</textarea>
      <button type="button" class="btn btn-secondary" data-mw-cmt-post ${u.cmt.trim() && !u.busy ? '' : 'disabled'}>${u.busy === 'cmt' ? 'Posting…' : 'Post'}</button>
    </div>
    ${msgLine(u)}`;
}, (host, w, data, ctx, redraw) => {
  const u = uiOf(String(w.id));
  const ta = host.querySelector<HTMLTextAreaElement>('[data-mw-cmt]');
  const post = host.querySelector<HTMLButtonElement>('[data-mw-cmt-post]');
  ta?.addEventListener('input', () => { u.cmt = ta.value; if (post) post.disabled = !ta.value.trim() || !!u.busy; ctx.markDirty(anyWoBusy()); });
  ta?.addEventListener('keydown', (e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); post?.click(); } });
  post?.addEventListener('click', async () => {
    const text = u.cmt.trim(); if (!text) return;
    await write(ctx, data, host, redraw, 'cmt', 'POST', `${WO_API}/${encodeURIComponent(w.id)}/comments`, { text, by: ctx.actorName() }, false, (j) => { if (data.wo && Array.isArray(j.comments)) data.wo.comments = j.comments; u.cmt = ''; });
  });
  host.querySelectorAll<HTMLElement>('[data-mw-cmt-rm]').forEach(el => el.addEventListener('click', async () => {
    await write(ctx, data, host, redraw, 'cmt', 'POST', `${WO_API}/${encodeURIComponent(w.id)}/comments/remove`, { comment_id: el.dataset.mwCmtRm, by: ctx.actorName() }, false, (j) => { if (data.wo && Array.isArray(j.comments)) data.wo.comments = j.comments; });
  }));
}, true);

// ── 7. Labor time ───────────────────────────────────────────────────────────
section('wo_labor', 'Labor time', 60, () => true, (w, data, ctx) => {
  const u = uiOf(String(w.id));
  const running = !!w.timer_started_at;
  const total = Number(w.total_minutes) || 0;
  let labor = '';
  if (seesMoney(ctx) && total > 0) {
    const nm = String(w.assigned_to || '').trim().toLowerCase();
    const v = nm ? data.vendors.find(x => String(x.name || '').trim().toLowerCase() === nm) : null;
    const rate = v && Number(v.hourly_rate) > 0 ? Number(v.hourly_rate) : null;
    if (rate) labor = `<span class="mw-muted" title="assignee hourly rate × tracked time — display only">💵 labor ≈ ${esc(fmtMoney(rate * total / 60))}</span>`;
  }
  return `
    <div class="mw-bar mw-wrap">
      <span class="mw-big">${esc(fmtMins(total))}</span>${running ? '<span class="mw-run">⏱ running…</span>' : ''}${labor}
    </div>
    <div class="mw-bar mw-wrap">
      <button type="button" class="btn ${running ? 'btn-secondary' : 'btn-secondary'} btn-sm" data-mw-timer="${running ? 'pause' : 'start'}" ${u.busy ? 'disabled' : ''}>${running ? '⏸ Pause' : '▶ Start timer'}</button>
      <input type="number" min="1" inputmode="numeric" class="mw-input mw-num" placeholder="min" aria-label="Minutes to log" data-mw-mins>
      <button type="button" class="btn btn-ghost btn-sm" data-mw-log ${u.busy ? 'disabled' : ''}>+ Log time</button>
    </div>
    ${msgLine(u)}`;
}, (host, w, data, ctx, redraw) => {
  host.querySelector<HTMLElement>('[data-mw-timer]')?.addEventListener('click', (e) => {
    const action = (e.currentTarget as HTMLElement).dataset.mwTimer!;
    void write(ctx, data, host, redraw, 'timer', 'POST', `${WO_API}/${encodeURIComponent(w.id)}/timer`, { action, by: ctx.actorName(), surface: 'calendar' }, false);
  });
  host.querySelector('[data-mw-log]')?.addEventListener('click', () => {
    const inp = host.querySelector<HTMLInputElement>('[data-mw-mins]');
    const minutes = Math.round(Number(inp && inp.value));
    if (!Number.isFinite(minutes) || minutes <= 0) { const u = uiOf(String(w.id)); u.err = 'Type the minutes to log first.'; redraw(); return; }
    void write(ctx, data, host, redraw, 'timer', 'POST', `${WO_API}/${encodeURIComponent(w.id)}/timer`, { action: 'manual', minutes, by: ctx.actorName(), surface: 'calendar' }, false);
  });
});

// ── 8. Vendor flow (no-login vendor link: quotes, progress, invoices, replies) ─
const revokeArmed = new Set<string>();
section('wo_vendor', 'Vendor flow', 70, () => true, (w, _data, ctx) => {
  const u = uiOf(String(w.id));
  const money = seesMoney(ctx);
  const quotes: any[] = Array.isArray(w.quotes) ? w.quotes : [];
  const replies: any[] = Array.isArray(w.vendor_replies) ? w.vendor_replies : [];
  const inv: any[] = (Array.isArray(w.invoices) ? w.invoices : []).filter((i: any) => i && i.source === 'vendor_link');
  const prog = w.vendor_progress, rep = w.vendor_report;
  const RESULT: Record<string, string> = { fixed: 'Fixed', partial: 'Partially fixed', not_fixed: 'Not fixed', skipped: "Couldn't get to it" };
  const has = quotes.length || replies.length || inv.length || prog || w.vendor_link || w.vendor_texted || rep;
  return `
    ${!has ? '<div class="mw-muted">No vendor link yet. Text the work order to a vendor (⋯ → Text a vendor…) — they quote, update progress and invoice from their phone, no login.</div>' : ''}
    <div class="mw-pills">
      ${w.vendor_texted ? pill(`texted ${esc(md(w.vendor_texted.at))}${w.vendor_texted.group && w.vendor_texted.group.n ? ` → group of ${esc(w.vendor_texted.group.n)}` : (w.vendor_texted.to ? ` → ${esc(w.vendor_texted.to)}` : '')}`) : ''}
      ${prog ? pill(`${esc(prog.done)}/${esc(prog.total)} items done${prog.all_done ? ' · vendor says complete' : ''}`, prog.all_done ? 'ok' : '') : ''}
      ${w.vendor_link && w.vendor_link.primary === false ? pill('bundled') : ''}
    </div>
    ${w.vendor_link ? (revokeArmed.has(String(w.id))
      ? `<div class="mw-askbox"><div class="mw-warn">Revoke the vendor's link? Their page stops working right away; texting the WO again mints a new link.</div><div class="mw-bar"><button type="button" class="btn btn-danger btn-sm" data-mw-vrevoke>🚫 Revoke now</button><button type="button" class="btn btn-ghost btn-sm" data-mw-vrevoke-cancel>Cancel</button></div></div>`
      : `<div class="mw-bar mw-wrap"><button type="button" class="btn btn-secondary btn-sm" data-mw-vlink>🔗 Copy vendor link</button><button type="button" class="btn btn-ghost btn-sm" data-mw-vrevoke>🚫 Revoke link</button></div>`) : ''}
    ${quotes.length ? `<div class="mw-h">Quotes</div>${quotes.map(q => `
      <div class="mw-qrow">
        <span>${money && q.total != null ? `<b>${esc(fmtMoney(q.total))}</b> · ` : ''}${esc(q.vendor_name || 'vendor')} · ${esc(md(q.at))}${q.site_visit ? ' · wants a site visit' : ''}</span>
        ${pill(esc(q.status || 'pending'), q.status === 'approved' ? 'ok' : q.status === 'declined' ? 'bad' : 'warn')}
        ${money && q.ai_check ? pill(q.ai_check.flag === 'ok' ? 'AI: looks fair' : q.ai_check.flag === 'high' ? 'AI: looks high' : 'AI: review', q.ai_check.flag === 'ok' ? 'ok' : q.ai_check.flag === 'high' ? 'bad' : 'warn') : ''}
        ${q.doc_url ? `<a href="${esc(q.doc_url)}" target="_blank" rel="noopener">📄 quote</a>` : ''}
        ${money && q.status === 'pending' ? `<span class="mw-bar"><button type="button" class="btn btn-secondary btn-sm" data-mw-quote="${esc(q.id)}|approve">✓ Approve</button><button type="button" class="btn btn-ghost btn-sm" data-mw-quote="${esc(q.id)}|decline">✕ Decline</button></span>` : ''}
        ${q.note ? `<div class="mw-muted">${esc(q.note)}</div>` : ''}
      </div>`).join('')}${!money && quotes.some(q => q.status === 'pending') ? '<div class="mw-fine">Quote amounts and approvals are for an admin or director.</div>' : ''}` : ''}
    ${inv.length ? `<div class="mw-h">Vendor invoices</div>${inv.map(i => `
      <div class="mw-qrow">
        <span>🧾 ${i.url ? `<a href="${esc(i.url)}" target="_blank" rel="noopener">${esc(i.name || 'invoice')}</a>` : esc(i.name || 'invoice')}${money && i.total != null ? ` · ${esc(fmtMoney(i.total))}` : ''}${i.invoice_no ? ` · #${esc(i.invoice_no)}` : ''}</span>
        ${i.status === 'accepted' ? pill('accepted', 'ok') : i.status === 'rejected' ? pill('rejected', 'bad') : i.id ? pill('awaiting acceptance', 'warn') : ''}
        ${money && i.id && i.status !== 'accepted' && i.status !== 'rejected' ? `<span class="mw-bar"><button type="button" class="btn btn-secondary btn-sm" data-mw-inv="${esc(i.id)}|accept">✓ Accept${i.total != null ? ` (${esc(fmtMoney(i.total))} → cost)` : ''}</button><button type="button" class="btn btn-ghost btn-sm" data-mw-inv="${esc(i.id)}|reject">✕ Reject</button></span>` : ''}
      </div>`).join('')}` : ''}
    ${rep ? `<div class="mw-h">Vendor report ${rep.result ? pill(esc(RESULT[rep.result] || rep.result), rep.result === 'fixed' ? 'ok' : rep.result === 'partial' ? 'warn' : 'bad') : ''}</div>${rep.work_done ? `<div class="mw-text">${esc(rep.work_done)}</div>` : ''}
      ${(rep.before || []).length || (rep.after || []).length ? mediaStrip([...(rep.before || []).map((x: string) => ({ url: x, caption: 'Vendor · before' })), ...(rep.after || []).map((x: string) => ({ url: x, caption: 'Vendor · after' }))].slice(0, 12), { size: 'sm', alt: 'vendor photo' }) : ''}` : ''}
    ${replies.length ? `<div class="mw-h">Messages</div>${vendorRepliesHtml(replies, { limit: 4 })}` : ''}
    ${msgLine(u)}`;
}, (host, w, data, ctx, redraw) => {
  const id = encodeURIComponent(String(w.id));
  bindMediaStrips(host);   // R116 #5: the vendor's before / after photos + clips open the viewer
  host.querySelector('[data-mw-vlink]')?.addEventListener('click', async (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    const link = w.vendor_link && w.vendor_link.token ? `${location.origin}/v/${w.vendor_link.token}` : '';
    if (!link) return;
    try { await navigator.clipboard.writeText(link); b.textContent = '✓ Copied'; } catch { const u = uiOf(String(w.id)); u.ok = `Vendor link: ${link}`; redraw(); }
  });
  host.querySelector('[data-mw-vrevoke]')?.addEventListener('click', () => {
    const wid = String(w.id);
    if (!revokeArmed.has(wid)) { revokeArmed.add(wid); redraw(); return; }   // first tap asks, inline
    revokeArmed.delete(wid);
    void write(ctx, data, host, redraw, 'vendor', 'DELETE', `${AO}/vendor-flow/work-orders/${id}/link`, { by: ctx.actorName() }, false, (_j, uu) => { uu.ok = 'Vendor link revoked — texting the WO again mints a new one.'; });
  });
  host.querySelector('[data-mw-vrevoke-cancel]')?.addEventListener('click', () => { revokeArmed.delete(String(w.id)); redraw(); });
  host.querySelectorAll<HTMLElement>('[data-mw-quote]').forEach(el => el.addEventListener('click', () => {
    const [qid, action] = (el.dataset.mwQuote || '').split('|');
    void write(ctx, data, host, redraw, 'vendor', 'POST', `${AO}/vendor-flow/work-orders/${id}/quotes/${encodeURIComponent(qid)}`, { action, note: '', by: ctx.actorName() }, false);
  }));
  host.querySelectorAll<HTMLElement>('[data-mw-inv]').forEach(el => el.addEventListener('click', () => {
    const [iid, action] = (el.dataset.mwInv || '').split('|');
    void write(ctx, data, host, redraw, 'vendor', 'POST', `${AO}/vendor-flow/work-orders/${id}/invoices/${encodeURIComponent(iid)}`, { action, note: '', by: ctx.actorName() }, false);
  }));
});

// ── 9. Owner portal (publish a done WO / ask the owner to approve) ─────────
const opAsk = new Map<string, { kind: 'publish' | 'request'; note: string; amount: string }>();
section('wo_owner', 'Owner portal', 75, () => true, (w, _data, ctx) => {
  const u = uiOf(String(w.id));
  const money = seesMoney(ctx);
  const op = w.owner_portal && w.owner_portal.state ? w.owner_portal : null;
  const LBL: Record<string, string> = { published: 'On the owner portal', needs_approval: 'Waiting on the owner’s approval', approved: 'Owner approved', declined: 'Owner declined' };
  const ask = opAsk.get(String(w.id));
  // the notes are free text that usually quote the amount ("…QuickBooks charge, $120") — money roles only
  const state = op ? `<div class="mw-text"><b>${esc(LBL[op.state] || op.state)}</b>${money && op.amount != null ? ` · ${esc(fmtMoney(op.amount))}` : ''}${money && op.note ? ` — ${esc(op.note)}` : ''}${money && op.decision_note ? `<div class="mw-muted">Owner: “${esc(op.decision_note)}”</div>` : ''}</div>` : '<div class="mw-muted">Not shown to the owner.</div>';
  if (!money) return `${state}<div class="mw-fine">Publishing to the owner (and the amounts) is done by an admin or director.</div>`;
  const st = op ? op.state : '';
  if (ask) {
    return `${state}
      <div class="mw-askbox">
        ${ask.kind === 'publish' && w.payer === 'wch' ? '<div class="mw-warn">WCH covers this one — it shows in the owner portal as “covered by WCH” (not billed).</div>' : ''}
        ${ask.kind === 'publish' && !w.payer ? '<div class="mw-warn">No payer is set — the owner will see it as billed to them. Set “Who pays” in Details first if that’s wrong.</div>' : ''}
        ${ask.kind === 'request' ? `<label class="mw-field">Amount for the owner to approve ($)<input type="number" min="0" step="0.01" inputmode="decimal" class="mw-input" data-mw-op-amount value="${esc(ask.amount)}"></label>` : ''}
        <label class="mw-field">${ask.kind === 'publish' ? 'Owner-facing note (optional)' : 'What are we asking them to approve? (owner-facing)'}<textarea class="mw-input" rows="2" data-mw-op-note>${esc(ask.note)}</textarea></label>
        <div class="mw-bar"><button type="button" class="btn btn-secondary btn-sm" data-mw-op-go ${u.busy ? 'disabled' : ''}>${u.busy === 'op' ? 'Sending…' : ask.kind === 'publish' ? '🏦 Publish to owner' : '🏦 Send for approval'}</button><button type="button" class="btn btn-ghost btn-sm" data-mw-op-cancel>Cancel</button></div>
      </div>${msgLine(u)}`;
  }
  return `${state}
    <div class="mw-bar mw-wrap">
      ${w.status === 'done' && st !== 'published' ? '<button type="button" class="btn btn-secondary btn-sm" data-mw-op="publish">🏦 Publish to owner</button>' : ''}
      ${w.status !== 'done' && st !== 'needs_approval' && st !== 'approved' ? '<button type="button" class="btn btn-secondary btn-sm" data-mw-op="request">🏦 Ask owner to approve</button>' : ''}
      ${st ? '<button type="button" class="btn btn-ghost btn-sm" data-mw-op="unpublish">↩ Unpublish</button>' : ''}
    </div>${msgLine(u)}`;
}, (host, w, data, ctx, redraw) => {
  const id = String(w.id);
  host.querySelectorAll<HTMLElement>('[data-mw-op]').forEach(el => el.addEventListener('click', () => {
    const kind = el.dataset.mwOp!;
    if (kind === 'unpublish') { void write(ctx, data, host, redraw, 'op', 'POST', `${WO_API}/${encodeURIComponent(id)}/owner-portal`, { action: 'unpublish', by: ctx.actorName() }, false); return; }
    opAsk.set(id, { kind: kind as 'publish' | 'request', note: kind === 'publish' ? String(w.summary || (w.completion && w.completion.what_done) || '') : String(w.description || ''), amount: w.cost != null ? String(w.cost) : '' });
    ctx.markDirty(true); redraw();
  }));
  const ask = opAsk.get(id);
  if (!ask) return;
  const note = host.querySelector<HTMLTextAreaElement>('[data-mw-op-note]'), amt = host.querySelector<HTMLInputElement>('[data-mw-op-amount]');
  note?.addEventListener('input', () => { ask.note = note.value; });
  amt?.addEventListener('input', () => { ask.amount = amt.value; });
  host.querySelector('[data-mw-op-cancel]')?.addEventListener('click', () => { opAsk.delete(id); ctx.markDirty(anyWoBusy()); redraw(); });
  host.querySelector('[data-mw-op-go]')?.addEventListener('click', async () => {
    const body: any = { by: ctx.actorName(), note: ask.note.trim() };
    if (ask.kind === 'publish') body.action = 'publish';
    else {
      const n = Number(ask.amount);
      if (ask.amount.trim() === '' || !Number.isFinite(n) || n < 0) { const u = uiOf(id); u.err = 'Enter the amount for the owner to approve.'; redraw(); return; }
      body.action = 'request_approval'; body.amount = n;
    }
    const j = await write(ctx, data, host, redraw, 'op', 'POST', `${WO_API}/${encodeURIComponent(id)}/owner-portal`, body, false, (r) => { opAsk.delete(id); if (data.wo) data.wo.owner_portal = r.owner_portal || null; });
    if (j) ctx.toast(ask.kind === 'publish' ? 'Published to the owner portal' : 'Sent to the owner for approval');
  });
});
export const ownerAskOpen = (): boolean => opAsk.size > 0;

// ── 10. Ground review (per-property expense review notes + reviewed stamp) ─
section('wo_review', 'Ground review', 80, () => true, (w) => {
  const u = uiOf(String(w.id));
  const notes: any[] = Array.isArray(w.review_notes) ? w.review_notes : [];
  const gr = w.ground_review;
  return `
    ${notes.slice(-5).map(n => `<div class="mw-cmt"><div class="mw-cmt-h"><b>${esc(n.by || '—')}</b> · ${esc(String(n.at || '').slice(0, 10))}</div><div class="mw-text">${esc(n.text)}</div></div>`).join('') || '<div class="mw-muted">No review notes.</div>'}
    <div class="mw-compose">
      <input type="text" class="mw-input" data-mw-rnote value="${esc(u.reviewNote)}" placeholder="Review note for this expense…" maxlength="500">
      <button type="button" class="btn btn-secondary btn-sm" data-mw-rnote-add ${u.reviewNote.trim() && !u.busy ? '' : 'disabled'}>Add</button>
    </div>
    <label class="mw-check"><input type="checkbox" data-mw-reviewed ${gr ? 'checked' : ''} ${u.busy ? 'disabled' : ''}> ✓ Reviewed${gr ? ` <span class="mw-muted">— ${esc(gr.by || '')} · ${esc(String(gr.at || '').slice(0, 10))}</span>` : ''}</label>
    ${msgLine(u)}`;
}, (host, w, data, ctx, redraw) => {
  const u = uiOf(String(w.id));
  const inp = host.querySelector<HTMLInputElement>('[data-mw-rnote]');
  const add = host.querySelector<HTMLButtonElement>('[data-mw-rnote-add]');
  inp?.addEventListener('input', () => { u.reviewNote = inp.value; if (add) add.disabled = !inp.value.trim() || !!u.busy; ctx.markDirty(anyWoBusy()); });
  add?.addEventListener('click', async () => {
    const text = u.reviewNote.trim(); if (!text) return;
    await write(ctx, data, host, redraw, 'review', 'POST', `${WO_API}/${encodeURIComponent(w.id)}/review-note`, { text, by: ctx.actorName() }, false, (j) => { if (data.wo && Array.isArray(j.review_notes)) data.wo.review_notes = j.review_notes; u.reviewNote = ''; });
  });
  host.querySelector<HTMLInputElement>('[data-mw-reviewed]')?.addEventListener('change', async (e) => {
    const on = (e.target as HTMLInputElement).checked;
    await write(ctx, data, host, redraw, 'review', 'POST', `${WO_API}/${encodeURIComponent(w.id)}/reviewed`, { on, by: ctx.actorName() }, false, (j) => { if (data.wo) data.wo.ground_review = j.ground_review || null; });
  });
});

// ── 11. Field access (emergency / urgent / incident) ───────────────────────
section('wo_field', 'Field access', 85, (w) => wantsField(w), (w, data) => {
  const f = data.field;
  if (!f) return '<div class="mw-muted">No access details could be loaded for this property.</div>';
  const a = f.access || {};
  const line = (k: string, v: unknown) => { const s = String(v == null ? '' : v).trim(); return s ? `<div class="mw-row"><span class="mw-k">${esc(k)}</span><span class="mw-v mw-mono">${esc(s)}</span></div>` : ''; };
  const lines = [line('Lockbox', a.lockbox), line('Backup code', a.backup_code), line('Gate', a.gate), line('Breaker', a.breaker), line('Breaker reset', a.breaker_reset), line('Water shutoff', a.water_shutoff), line('Water heater', a.water_heater), line('HVAC', a.hvac), line('Safety', a.safety)].join('');
  return `${f.occupied ? `<div class="mw-warn">⚠ ${esc(f.guest_present_note || 'A guest is in the home.')}</div>` : ''}<div class="mw-kv">${lines || '<div class="mw-muted">No access details on file for this property.</div>'}</div>`;
}, () => { /* read-only */ });

// ── 12. History ─────────────────────────────────────────────────────────────
section('wo_log', 'History', 95, (w) => Array.isArray(w.log) && w.log.length > 0, (w) => {
  const log: any[] = (w.log || []).slice(-15).reverse();
  return `<ul class="mw-log">${log.map(l => `<li><span class="mw-muted">${esc(String(l.at || '').slice(0, 16).replace('T', ' '))}</span> ${esc(l.note || l.action || l.status || '')}${l.by ? ` <span class="mw-muted">(${esc(l.by)})</span>` : ''}</li>`).join('')}</ul>`;
}, () => { /* read-only */ });

// ── head actions ────────────────────────────────────────────────────────────
const W = (data: unknown): any => { const d = data as WoData | null; return d && d.wo ? d.wo : null; };
const open = (w: any) => !!w && w.status !== 'done';
registerAction({ key: 'wo_book', inspector: 'wo', label: 'Book a day…', icon: '📆', order: 10, roles: OFFICE, primaryIn: ALL_VIEWS,
  when: (_r, d) => { const w = W(d); return open(w) && !schedOf(w); },
  run: (_r, d, ctx) => { const w = W(d); if (w) pickAndBook(ctx, target(w)); } });
registerAction({ key: 'wo_move', inspector: 'wo', label: 'Move to another day…', icon: '📆', order: 11, roles: OFFICE, primaryIn: ALL_VIEWS,
  when: (_r, d) => { const w = W(d); return open(w) && !!schedOf(w); },
  run: (_r, d, ctx) => { const w = W(d); if (w) pickAndBook(ctx, target(w)); } });
registerAction({ key: 'wo_reopen', inspector: 'wo', label: 'Reopen', icon: '↺', order: 12, roles: OFFICE, primaryIn: ALL_VIEWS,
  when: (_r, d) => { const w = W(d); return !!w && w.status === 'done'; },
  run: (_r, d, ctx) => { const w = W(d); if (!w) return; void (async () => {
    const r = await api('POST', `${WO_API}/${encodeURIComponent(w.id)}`, { status: 'open', by: ctx.actorName(), surface: 'calendar' });
    ctx.toast(r.ok ? `Reopened “${w.title}”` : `Couldn't reopen — ${r.error}`);
    if (r.ok) await ctx.reload({ base: true, layers: [LAYER_ID], inspector: true });
  })(); } });
registerAction({ key: 'wo_done', inspector: 'wo', label: 'Mark done', icon: '✓', order: 15, roles: OFFICE,
  when: (_r, d) => open(W(d)),
  run: (_r, d, ctx) => { const w = W(d); if (!w) return; void (async () => {
    const r = await api('POST', `${WO_API}/${encodeURIComponent(w.id)}`, { status: 'done', by: ctx.actorName(), surface: 'calendar' });
    if (!r.ok) { ctx.toast(`Couldn't mark it done — ${r.error}`); return; }
    const prev = String(w.status || 'open');
    ctx.toastUndo(`Marked “${w.title}” done`, () => { void api('POST', `${WO_API}/${encodeURIComponent(w.id)}`, { status: prev, by: ctx.actorName(), surface: 'calendar', note: 'reopened (undo from the calendar)' }).then(u => { ctx.toast(u.ok ? 'Reopened' : `Undo failed — ${u.error}`); return ctx.reload({ base: true, layers: [LAYER_ID], inspector: true }); }); });
    await ctx.reload({ base: true, layers: [LAYER_ID], inspector: true });
  })(); } });
registerAction({ key: 'wo_unbook', inspector: 'wo', label: 'Unbook (back to unscheduled)', icon: '↩', order: 20, roles: OFFICE,
  when: (_r, d) => { const w = W(d); return open(w) && !!schedOf(w); },
  run: (_r, d, ctx) => { const w = W(d); if (w) void bookWo(ctx, target(w), null); } });
registerAction({ key: 'wo_open_mn', inspector: 'wo', label: 'Open in Maintenance', icon: '🔧', order: 30, roles: OFFICE,
  when: (_r, d) => !!W(d),
  run: (_r, d) => { const w = W(d); if (w) WoQuickView.focusInMaintenance(String(w.id)); } });
// R115 review (A2): "Text a vendor" opens the Text-vendor form for THIS work order; "Copy vendor link" lives in the
// Vendor flow section only. R116 #2: the form is the shared TextVendorSheet, opened right here in the calendar's
// sheet (no hop to Maintenance) — vendor + team members with a phone on file + typed numbers as ONE Quo group text.
registerAction({ key: 'wo_text_vendor', inspector: 'wo', label: 'Text a vendor…', icon: '📲', order: 40, roles: OFFICE,
  when: (_r, d) => open(W(d)),
  run: (_r, d, ctx) => {
    const w = W(d); if (!w) return;
    const body = ctx.openSheet('<div class="mw-muted">Loading…</div>', { title: 'Text a vendor', onClose: () => ctx.markDirty(false) });
    mountTextVendor(body, {
      wo: w, by: ctx.actorName(), close: () => ctx.closeSheet(), onDirty: (dirty) => ctx.markDirty(dirty),
      onDone: async (kind) => { if (kind === 'sent') ctx.toast(`Texted “${w.title}”`); await ctx.reload({ inspector: true }); },
    });
  } });
registerAction({ key: 'wo_delete', inspector: 'wo', label: 'Delete…', icon: '🗑', order: 90, roles: OFFICE,
  when: (_r, d) => !!W(d),
  run: (_r, d, ctx) => {
    const w = W(d); if (!w) return;
    const sheet = ctx.openSheet(`
      <div class="mw mw-sheet">
        <p>Delete “<b>${ctx.esc(w.title)}</b>” at ${ctx.esc(w.listing_name || '')}? This cannot be undone — its photos, comments and history go with it.</p>
        <div class="mw-err" data-mw-err hidden></div>
        <div class="mw-sheet-foot" data-bottom-bar>
          <button type="button" class="btn btn-secondary" data-mw-cancel>Keep it</button>
          <button type="button" class="btn btn-danger" data-mw-del>Delete work order</button>
        </div>
      </div>`, { title: 'Delete work order' });
    sheet.querySelector('[data-mw-cancel]')?.addEventListener('click', () => ctx.closeSheet());
    const del = sheet.querySelector<HTMLButtonElement>('[data-mw-del]');
    del?.addEventListener('click', async () => {
      if (!del) return;
      del.disabled = true; del.textContent = 'Deleting…';
      const r = await api('DELETE', `${WO_API}/${encodeURIComponent(w.id)}`);
      if (!r.ok) { const e = sheet.querySelector<HTMLElement>('[data-mw-err]'); if (e) { e.hidden = false; e.textContent = r.error; } del.disabled = false; del.textContent = 'Delete work order'; return; }
      ui.delete(String(w.id));
      ctx.closeSheet(); ctx.closeInspector();
      ctx.toast(`Deleted “${w.title}”`);
      await ctx.reload({ base: true, layers: [LAYER_ID] });
    });
  } });

/** Anything typed-and-unsaved or in flight in a WO editor (the page's auto-refresh veto). */
export const woEditorBusy = (): boolean => anyWoBusy() || opAsk.size > 0 || [...ui.values()].some(u => hasUnsaved(u));
